import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex } from "theme-ui";

import { themevals } from "theme/themevals";
import BasicModal from "components/ui/BasicModal";
import { useToast } from "components/toast";
import { getConvertPreview, convertLead, type ConvertPreview } from "api/Leads";

/**
 * Convert to client (LEADS_MODULE_SCOPE.md s.16.4, artboard "Convert").
 * Nothing is copied. Status becomes Won; optionally invite a portal user
 * and create the Zoho Books customer; the company defaults the client will
 * get are shown and editable (s.12.5). Redirects to the client record.
 */

const input: React.CSSProperties = { padding: "7px 9px", border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: 6, fontSize: 13, fontFamily: "Poppins", width: "100%", boxSizing: "border-box" };
const row: React.CSSProperties = { display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 12, textAlign: "left", fontFamily: "Poppins", fontSize: 13 };
const check: React.CSSProperties = { width: 16, marginTop: 3, flexShrink: 0 };

export default function ConvertDialog({ leadId, leadName, convertedBy, onClose }: { leadId: string; leadName: string; convertedBy: string; onClose: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [preview, setPreview] = useState<ConvertPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [invite, setInvite] = useState(true);
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [zoho, setZoho] = useState(true);
  const [changing, setChanging] = useState(false);
  const [terms, setTerms] = useState<string>("");
  const [requiresDeposit, setRequiresDeposit] = useState(true);
  const [deposit, setDeposit] = useState<string>("50");
  const [portalLocations, setPortalLocations] = useState(false);
  const [portalCalculator, setPortalCalculator] = useState(false);

  useEffect(() => {
    getConvertPreview(leadId).then((p: any) => {
      if (p?.error) return toast.add(p.error, "var(--theme-ui-colors-danger)");
      setPreview(p);
      setFirst(p.invite.firstName);
      setLast(p.invite.lastName);
      setEmail(p.invite.email);
      setInvite(p.invite.possible);
      setTerms(p.defaults.terms != null ? String(p.defaults.terms) : "");
      setRequiresDeposit(p.defaults.requiresDeposit);
      setDeposit(String(p.defaults.depositPercentage));
      setPortalLocations(p.defaults.portalLocationsEnabled);
      setPortalCalculator(p.defaults.portalCalculatorEnabled);
    });
  }, [leadId]); // eslint-disable-line

  const submit = async () => {
    if (busy) return;
    if (invite && (!first.trim() || !email.trim())) return toast.add("A first name and email are needed for the invite.", "var(--theme-ui-colors-danger)");
    setBusy(true);
    const res: any = await convertLead(leadId, {
      invite,
      inviteFirstName: first.trim(),
      inviteLastName: last.trim(),
      inviteEmail: email.trim(),
      createZoho: zoho,
      terms: terms.trim() === "" ? null : Number(terms),
      requiresDeposit,
      depositPercentage: requiresDeposit ? Number(deposit || 50) : null,
      portalLocationsEnabled: portalLocations,
      portalCalculatorEnabled: portalCalculator,
    });
    setBusy(false);
    if (res?.error) return toast.add(res.error, "var(--theme-ui-colors-danger)");
    toast.add(res.joinedExisting ? `${leadName}'s contact added to client ${preview?.existingClient?.name ?? ""}.` : `${leadName} is now a client.`, "var(--theme-ui-colors-success)");
    navigate(`/clients/${res.clientId}`);
  };

  const joining = !!preview?.existingClient;

  return (
    <BasicModal
      title={joining ? `Add ${preview?.lead.contactName || "this contact"} to client ${preview?.existingClient?.name}` : `Convert ${leadName} to a client`}
      size="md"
      cancel_click={onClose}
      cancel_text="Cancel"
      confirm_text={busy ? "Converting\u2026" : joining ? "Add to the client" : "Convert to client"}
      confirm_click={submit}
      submitting={busy}
    >
      {joining ? (
        <Box sx={{ fontSize: 13, fontFamily: "Poppins", textAlign: "left", mb: 14, padding: "10px 12px", background: "#EEF4FB", border: "1px solid #B9CFEA", borderRadius: 6 }}>
          <b>{preview?.lead.name} is already client {preview?.existingClient?.name}</b> ({preview?.existingClient?.reason}).
          <Box sx={{ color: themevals.colors.dkgrey, mt: "4px" }}>
            Converting adds {preview?.lead.contactName || "this contact"} as a contact of that client and moves the RFQs, quotes, proposals, notes and sites of this lead to it. Nothing on the client changes. Quotes stay as issued.
          </Box>
        </Box>
      ) : (
        <Box sx={{ fontSize: 13, fontFamily: "Poppins", textAlign: "left", color: themevals.colors.dkgrey, mb: 14 }}>
          Nothing is copied or moved. The record keeps its RFQs, quotes, proposals and history; it stops being a lead and starts showing up with clients. {preview?.lead.contactName ? `${preview.lead.contactName} becomes its first contact.` : ""}
        </Box>
      )}
      {preview && preview.siblings.length > 0 && (
        <Box sx={{ fontSize: 12, fontFamily: "Poppins", textAlign: "left", color: themevals.colors.dkgrey, mb: 14 }}>
          {preview.siblings.length} other lead{preview.siblings.length === 1 ? "" : "s"} at this organization ({preview.siblings.map((s) => s.contactName || s.name).join(", ")}) stay{preview.siblings.length === 1 ? "s" : ""} a lead and will join the same client when converted.
        </Box>
      )}
      {!joining && (
        <Box sx={{ fontSize: 13, fontFamily: "Poppins", textAlign: "left", mb: 14 }}>
          Status becomes <b>Won</b> &middot; converted by {convertedBy} today
        </Box>
      )}

      <label style={row}>
        <input type="checkbox" style={check} checked={invite} onChange={(e) => setInvite(e.target.checked)} />
        <span>
          <b>Invite a portal user now</b>
          <Box sx={{ fontSize: 12, color: themevals.colors.dkgrey }}>They get a sign-in email and can see quotes, projects and invoices. Leave unticked to convert without a login, like many existing clients.</Box>
        </span>
      </label>
      {invite && (
        <Flex sx={{ gap: 8, mb: 12 }}>
          <input style={input} placeholder="First name" value={first} onChange={(e) => setFirst(e.target.value)} />
          <input style={input} placeholder="Last name" value={last} onChange={(e) => setLast(e.target.value)} />
          <input style={input} placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Flex>
      )}

      {!joining && (
      <label style={row}>
        <input type="checkbox" style={check} checked={zoho} onChange={(e) => setZoho(e.target.checked)} />
        <span>
          <b>Create the Zoho Books customer now</b>
          <Box sx={{ fontSize: 12, color: themevals.colors.dkgrey }}>Needed before the first invoice. Can also happen automatically when the first invoice is sent.</Box>
        </span>
      </label>
      )}

      {!joining && (
      <Box sx={{ textAlign: "left", fontSize: 13, fontFamily: "Poppins", borderTop: "1px solid", borderColor: "inputOutline", pt: 12 }}>
        <Flex sx={{ justifyContent: "space-between", alignItems: "center", mb: "6px" }}>
          <b>They start with</b>
          <button type="button" style={{ background: "none", border: "none", color: "#185FA5", cursor: "pointer", fontFamily: "Poppins", fontSize: 12, fontWeight: 500 }} onClick={() => setChanging((v) => !v)}>
            {changing ? "Done" : "Change"}
          </button>
        </Flex>
        {!changing ? (
          <Box sx={{ color: themevals.colors.dkgrey, lineHeight: 1.7 }}>
            Terms: {terms ? `Net ${terms}` : "company default"} &middot; Deposit: {requiresDeposit ? `${deposit || 50}% required` : "not required"} &middot; Portal locations: {portalLocations ? "on" : "off"} &middot; Calculator: {portalCalculator ? "on" : "off"}
          </Box>
        ) : (
          <Box>
            <Flex sx={{ gap: 8, mb: "8px", alignItems: "center" }}>
              <span style={{ width: 120 }}>Terms (days)</span>
              <input style={{ ...input, width: 100 }} type="number" min={0} value={terms} placeholder="default" onChange={(e) => setTerms(e.target.value)} />
            </Flex>
            <label style={{ ...row, alignItems: "center" }}>
              <input type="checkbox" style={{ width: 16 }} checked={requiresDeposit} onChange={(e) => setRequiresDeposit(e.target.checked)} /> Requires a deposit
              {requiresDeposit && <input style={{ ...input, width: 80, marginLeft: 8 }} type="number" min={1} max={100} value={deposit} onChange={(e) => setDeposit(e.target.value)} />}
              {requiresDeposit && <span>%</span>}
            </label>
            <label style={{ ...row, alignItems: "center" }}>
              <input type="checkbox" style={{ width: 16 }} checked={portalLocations} onChange={(e) => setPortalLocations(e.target.checked)} /> Portal locations page
            </label>
            <label style={{ ...row, alignItems: "center" }}>
              <input type="checkbox" style={{ width: 16 }} checked={portalCalculator} onChange={(e) => setPortalCalculator(e.target.checked)} /> Portal mulch calculator
            </label>
          </Box>
        )}
      </Box>
      )}
      {!preview && <Box sx={{ fontSize: 12, fontFamily: "Poppins", color: themevals.colors.dkgrey, mt: 8 }}>Loading…</Box>}
    </BasicModal>
  );
}
